import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { vacAge } from '../../constants/trades';
import { DEVISE, fmtH } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';
import {
  JOURNEE_MAX,
  finDeJournee,
  heuresParSemaine,
  journeeEntre,
  pauseDuContrat,
} from '../../lib/pointage';
import { FichierDossier } from './FichierDossier.jsx';
import { LieuxEmploye } from '../lieux/LieuxEmploye.jsx';
import { useModeConnexion } from '../../api/modeConnexion';

export function EmpEditor({
  empForm,
  setEmpForm,
  COLS,
  onSave,
  onDelete,
  estPatron,
  forme,
  magasin = false,
}) {
  const f = empForm;
  // Le code à 4 chiffres n'ouvre l'espace de chantier que tant que l'ouvrier
  // entre encore par « Code ouvrier ».
  const codeOuvrier = useModeConnexion()?.codeOuvrier === true;
  const set = (k, v) => setEmpForm((p) => ({ ...p, [k]: v }));
  const Sec = (t) => (
    <div
      style={{
        fontSize: 10,
        fontWeight: 800,
        color: C.navy,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        margin: '16px 0 8px',
        borderTop: '1px solid ' + C.border,
        paddingTop: 12,
      }}
    >
      {t}
    </div>
  );
  const Sel = (k, opts, def) => (
    <select
      value={f[k] || def || ''}
      onChange={(e) => set(k, e.target.value)}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        border: '1px solid ' + C.border,
        borderRadius: 9,
        padding: '10px',
        fontSize: 11,
        background: '#fff',
      }}
    >
      {opts.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
  return (
    <Card style={{ border: '2px solid ' + C.navy }}>
      <Bar icon="👷" title={f.id ? "Modifier l'employé" : 'Nouvel employé'} color={C.navy} />
      <div style={{ padding: 13 }}>
        {Sec('👤 Identité')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp label="Nom" value={f.nom || ''} onChange={(e) => set('nom', e.target.value)} />
          <Inp
            label="Prénom"
            value={f.prenom || ''}
            onChange={(e) => set('prenom', e.target.value)}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="Date de naissance"
            type="date"
            value={f.dateNaissance || ''}
            onChange={(e) => {
              const dn = e.target.value;
              setEmpForm((p) => {
                const v = vacAge(dn);
                return { ...p, dateNaissance: dn, ...(v && !p.conges ? { conges: v } : {}) };
              });
            }}
          />
          <Inp label="Sexe">{Sel('sexe', ['', 'Homme', 'Femme'])}</Inp>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="Nationalité"
            value={f.nationalite || ''}
            onChange={(e) => set('nationalite', e.target.value)}
          />
          <Inp label="État civil">
            {Sel('etatCivil', [
              '',
              'Célibataire',
              'Marié·e',
              'Divorcé·e',
              'Veuf·ve',
              'Partenariat',
            ])}
          </Inp>
        </div>
        <Inp
          label="Adresse"
          value={f.adresse || ''}
          onChange={(e) => set('adresse', e.target.value)}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
          <Inp label="NPA" value={f.npa || ''} onChange={(e) => set('npa', e.target.value)} />
          <Inp label="Ville" value={f.ville || ''} onChange={(e) => set('ville', e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp label="Téléphone" value={f.tel || ''} onChange={(e) => set('tel', e.target.value)} />
          <Inp
            label="E-mail"
            value={f.email || ''}
            onChange={(e) => set('email', e.target.value)}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp label="Langue">
            {Sel(
              'langue',
              ['Français', 'Allemand', 'Italien', 'Anglais', 'Portugais', 'Espagnol'],
              'Français',
            )}
          </Inp>
          <Inp
            label="N° AVS"
            value={f.avs || ''}
            onChange={(e) => set('avs', e.target.value)}
            placeholder="756.1234.5678.90"
          />
        </div>
        <div
          style={{ fontSize: 8, color: C.muted, marginTop: -4, marginBottom: 2, lineHeight: 1.5 }}
        >
          ℹ️ Le N° AVS est saisi ici une seule fois. Il est repris automatiquement sur la fiche de
          salaire, le contrat et le certificat annuel.
        </div>

        {Sec('📄 Contrat')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="Fonction"
            value={f.role || ''}
            onChange={(e) => set('role', e.target.value)}
            placeholder="Maçon, Chef projet…"
          />
          <Inp
            label="Métier / branche"
            value={f.metier || ''}
            onChange={(e) => set('metier', e.target.value)}
            placeholder="Gros œuvre…"
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp label="Type de contrat">
            {Sel('typeContrat', ['CDI', 'CDD', 'Temporaire', "À l'heure", 'Mensuel'], 'CDI')}
          </Inp>
          <Inp
            label="Date d'entrée"
            type="date"
            value={f.dateEmbauche || ''}
            onChange={(e) => set('dateEmbauche', e.target.value)}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="Fin (si CDD)"
            type="date"
            value={f.dateFin || ''}
            onChange={(e) => set('dateFin', e.target.value)}
          />
          <Inp
            label="Période d'essai"
            value={f.essai || ''}
            onChange={(e) => set('essai', e.target.value)}
            placeholder="3 mois"
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="Délai de résiliation"
            value={f.delaiResil || ''}
            onChange={(e) => set('delaiResil', e.target.value)}
            placeholder="1 mois"
          />
          <Inp
            label="Lieu de travail"
            value={f.lieu || ''}
            onChange={(e) => set('lieu', e.target.value)}
          />
        </div>
        <Inp
          label="Responsable / chef"
          value={f.responsable || ''}
          onChange={(e) => set('responsable', e.target.value)}
        />
        {/* Imprimées en dernier article du contrat officiel. */}
        <Inp
          label="Clauses particulières (ajoutées au contrat)"
          rows={3}
          value={f.clausesContrat || ''}
          onChange={(e) => set('clausesContrat', e.target.value)}
          placeholder="Véhicule de service, outillage, frais de repas…"
        />

        {Sec(tr('📍 Où travaille-t-il ?'))}
        <LieuxEmploye f={f} set={set} />

        {Sec('⏱ Temps & rémunération')}
        {/* L'horaire s'écrit une fois au contrat — arrivée, pause payée du
            matin, pause de midi, fin — et tout s'en calcule : la journée (la
            pause de midi déduite, celle du matin comptée), la semaine, l'heure
            de départ et les heures pointées. Chaque société a le sien. */}
        {(() => {
          const selStyle = {
            width: '100%',
            boxSizing: 'border-box',
            border: '1px solid ' + C.border,
            borderRadius: 9,
            padding: '10px',
            fontSize: 11,
            background: '#fff',
          };
          const calcule = {
            boxSizing: 'border-box',
            border: '1px solid ' + C.border,
            borderRadius: 9,
            padding: '10px',
            fontSize: 11,
            background: C.navyXL,
            color: C.ink,
          };
          const arrivee = f.heureDebut || '07:00';
          const finDe = (x) =>
            x.heureFin || finDeJournee(x.heureDebut || '07:00', x.heuresJour ?? 8, x);
          const fin = finDe(f);
          const jour = journeeEntre(arrivee, fin, f.pauseMin ?? 60);
          const trop = jour != null && jour > JOURNEE_MAX;
          // Toute retouche de l'horaire recalcule la journée et la semaine.
          const majHoraire = (champs) =>
            setEmpForm((p) => {
              const n = { ...p, heureFin: finDe(p), ...champs };
              const j = journeeEntre(n.heureDebut || '07:00', n.heureFin, n.pauseMin ?? 60);
              if (j != null) n.heuresJour = j;
              n.hSem = heuresParSemaine(n.heuresJour ?? 8, n.taux ?? 100);
              return n;
            });
          const pz = pauseDuContrat(f);
          const [ph, pm] = pz.debut.split(':').map(Number);
          const plus = (debut, min) => {
            const [h, m] = String(debut || '00:00')
              .split(':')
              .map(Number);
            const t = h * 60 + (m || 0) + (+min || 0);
            return (
              String(Math.floor(t / 60) % 24).padStart(2, '0') +
              ':' +
              String(t % 60).padStart(2, '0')
            );
          };
          const payee = +f.pausePayeeMin || 0;
          const debutPayee = f.pausePayeeDebut || '09:00';
          return (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <Inp
                  label="Taux %"
                  type="number"
                  value={f.taux ?? 100}
                  onChange={(e) => majHoraire({ taux: +e.target.value })}
                />
                <Inp label="Heures/jour (calculé)">
                  <div aria-label="Heures par jour" style={calcule}>
                    {jour != null ? fmtH(jour) : '—'}
                  </div>
                </Inp>
                <Inp label="Heures/sem (calculé)">
                  <div aria-label="Heures par semaine" style={calcule}>
                    {fmtH(heuresParSemaine(jour ?? f.heuresJour ?? 8, f.taux ?? 100))}
                  </div>
                </Inp>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Inp
                  label="Arrivée"
                  type="time"
                  value={arrivee}
                  onChange={(e) => majHoraire({ heureDebut: e.target.value })}
                />
                <Inp
                  label="Fin de journée"
                  type="time"
                  value={fin}
                  onChange={(e) => majHoraire({ heureFin: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Inp
                  label="Pause du matin dès (payée)"
                  type="time"
                  value={debutPayee}
                  onChange={(e) => set('pausePayeeDebut', e.target.value)}
                />
                <Inp label="Pause du matin">
                  <select
                    value={String(payee)}
                    onChange={(e) => set('pausePayeeMin', +e.target.value)}
                    aria-label="Durée de la pause du matin"
                    style={selStyle}
                  >
                    {[0, 10, 15, 20, 30].map((m) => (
                      <option key={m} value={String(m)}>
                        {m === 0 ? 'Aucune' : m + ' min, payée'}
                      </option>
                    ))}
                    {![0, 10, 15, 20, 30].includes(payee) && (
                      <option value={String(payee)}>{payee} min, payée</option>
                    )}
                  </select>
                </Inp>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Inp
                  label="Pause de midi dès"
                  type="time"
                  value={f.pauseDebut || '12:00'}
                  onChange={(e) => set('pauseDebut', e.target.value)}
                />
                <Inp label="Pause de midi (non payée)">
                  <select
                    value={String(f.pauseMin ?? 60)}
                    onChange={(e) => majHoraire({ pauseMin: +e.target.value })}
                    aria-label="Durée de la pause"
                    style={selStyle}
                  >
                    {[30, 45, 60, 90].map((m) => (
                      <option key={m} value={String(m)}>
                        {m === 60 ? '1 h' : m === 90 ? '1 h 30' : m + ' min'}
                      </option>
                    ))}
                    {f.pauseMin != null && ![30, 45, 60, 90].includes(+f.pauseMin) && (
                      <option value={String(f.pauseMin)}>{f.pauseMin} min</option>
                    )}
                  </select>
                </Inp>
              </div>
              <div
                role={trop || jour == null ? 'alert' : undefined}
                style={{
                  fontSize: 10,
                  color: trop || jour == null ? C.red : C.ink,
                  background: trop || jour == null ? C.red + '14' : C.navyXL,
                  borderRadius: 9,
                  padding: '8px 10px',
                  margin: '-2px 0 10px',
                  lineHeight: 1.5,
                }}
              >
                {jour == null ? (
                  <>La fin de journée doit venir après l'arrivée et la pause de midi.</>
                ) : (
                  <>
                    🕖 Horaire du contrat : <b>{arrivee}</b> → <b>{fin}</b>
                    {payee > 0 && (
                      <>
                        {' '}
                        · pause du matin{' '}
                        <b>
                          {debutPayee} – {plus(debutPayee, payee)}
                        </b>{' '}
                        payée
                      </>
                    )}{' '}
                    · midi{' '}
                    <b>
                      {pz.debut} – {plus(String(ph) + ':' + String(pm || 0), pz.minutes)}
                    </b>{' '}
                    non payée · <b>{fmtH(jour)}</b> de travail par jour. Les heures pointées se
                    calculent sur cet horaire.
                    {trop && <> La journée dépasse neuf heures : raccourcis-la.</>}
                  </>
                )}
              </div>
            </>
          );
        })()}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label={'Taux horaire ' + DEVISE + '/h'}
            type="number"
            value={f.txH || 0}
            onChange={(e) => set('txH', +e.target.value)}
          />
          <Inp
            label={'Salaire mensuel ' + DEVISE}
            type="number"
            value={f.salBase || 0}
            onChange={(e) => set('salBase', +e.target.value)}
          />
        </div>
        <div style={{ fontSize: 8, color: C.grey, lineHeight: 1.5, marginBottom: 2 }}>
          Salaire = heures pointées × taux horaire. Le mensuel sert de forfait si aucune heure n'est
          pointée, ou si le taux horaire est à 0 (salarié au mois qui pointe ses heures).
        </div>
        <Inp label="13e salaire ?">
          <select
            value={f.sal13 ? 'Oui' : 'Non'}
            onChange={(e) => set('sal13', e.target.value === 'Oui')}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '10px',
              fontSize: 11,
              background: '#fff',
            }}
          >
            <option value="Non">Non</option>
            <option value="Oui">Oui</option>
          </select>
        </Inp>

        {Sec('🌴 Vacances')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="Jours congés / an"
            type="number"
            value={f.conges || 0}
            onChange={(e) => set('conges', +e.target.value)}
          />
          <Inp
            label="Solde initial (jours)"
            type="number"
            value={f.soldeInit || 0}
            onChange={(e) => set('soldeInit', +e.target.value)}
          />
        </div>
        <Inp
          label="Taux vacances % (indemnité)"
          type="number"
          value={f.tauxVac || 0}
          onChange={(e) => set('tauxVac', +e.target.value)}
        />
        <div style={{ fontSize: 8, color: C.grey, lineHeight: 1.5, marginBottom: 2 }}>
          Ex : 8.33% (4 semaines) ou 10.64% (5 semaines). Sert au calcul de l'indemnité vacances
          (basée sur le salaire).
        </div>
        {f.dateNaissance && vacAge(f.dateNaissance) && (
          <div style={{ fontSize: 8, color: C.grey, lineHeight: 1.5, marginTop: -4 }}>
            🌴 Selon l'âge (CN construction) : ~{vacAge(f.dateNaissance)} jours. Modifiable.
          </div>
        )}

        {Sec('🛡️ Assurances & impôt à la source')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="LPP 2e pilier (%)"
            type="number"
            value={f.lpp ?? 5}
            onChange={(e) => set('lpp', +e.target.value)}
          />
          <Inp label="LAA — accidents">
            {Sel('laa', ['Prof. + non-prof.', 'Prof. uniquement'], 'Prof. + non-prof.')}
          </Inp>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="Impôt à la source (%)"
            type="number"
            value={f.is || 0}
            onChange={(e) => set('is', +e.target.value)}
          />
          <Inp
            label="Canton (impôt)"
            value={f.canton || ''}
            onChange={(e) => set('canton', e.target.value)}
            placeholder="GE, VD…"
          />
        </div>
        {estPatron || f.patronIndependant ? (
          <StatutDuPatron
            forme={forme}
            soumisAC={f.soumisAC !== false}
            soumisLPP={f.soumisLPP !== false}
            onIndependant={() => setEmpForm((p) => ({ ...p, soumisAC: false, soumisLPP: false }))}
          />
        ) : null}
        <div style={{ background: C.navyXL, borderRadius: 9, padding: '10px 11px', marginTop: 2 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.navy, marginBottom: 6 }}>
            Soumis aux assurances (par défaut : oui)
          </div>
          {[
            ['soumisAVS', 'AVS / AI / APG'],
            ['soumisAC', 'Assurance chômage (AC)'],
            ['soumisLAA', 'Accidents (LAA)'],
            ['soumisLPP', '2e pilier (LPP)'],
            ['soumisIS', 'Impôt à la source'],
          ].map(([k, l]) => {
            // Le patron d'une raison individuelle : chômage et LPP décochés
            // tant qu'il ne les coche pas lui-même (le patron décide), comme
            // le calcul des charges (lib/retenues, RegimeDuPatron).
            const indep = f.patronIndependant || (estPatron && estIndependant(forme));
            const on =
              indep && (k === 'soumisAC' || k === 'soumisLPP') ? f[k] === true : f[k] !== false;
            return (
              <div
                key={k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '3px 0',
                }}
              >
                <span style={{ fontSize: 9.5, color: C.grey }}>{l}</span>
                <div
                  role="switch"
                  aria-checked={on}
                  aria-label={l}
                  onClick={() => set(k, !on)}
                  style={{
                    width: 40,
                    height: 22,
                    borderRadius: 99,
                    background: on ? C.green : C.border,
                    position: 'relative',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: on ? 20 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: '#fff',
                      transition: 'left .15s',
                    }}
                  />
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 7.5, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
            Les <b>taux</b> (AVS 5.3 %, AC 1.1 %, LAA, LPP…) viennent des{' '}
            <b>Réglages de l'entreprise</b> — pas besoin de les saisir pour chaque employé. Ces
            interrupteurs disent seulement si l'employé y est soumis.
          </div>
        </div>

        {Sec('👶 Enfants / Allocations familiales')}
        {(f.enfantsList || []).map((en, ix) => (
          <div
            key={ix}
            style={{
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '9px 10px',
              marginBottom: 7,
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 7,
                right: 9,
                fontSize: 9,
                color: C.red,
                cursor: 'pointer',
                fontWeight: 700,
              }}
              onClick={() =>
                set(
                  'enfantsList',
                  (f.enfantsList || []).filter((_, j) => j !== ix),
                )
              }
            >
              ✕ retirer
            </div>
            <div style={{ fontSize: 8, fontWeight: 800, color: C.muted, marginBottom: 4 }}>
              Enfant {ix + 1}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              <Inp
                label="Nom"
                value={en.nom || ''}
                onChange={(e) => {
                  const l = [...(f.enfantsList || [])];
                  l[ix] = { ...l[ix], nom: e.target.value };
                  set('enfantsList', l);
                }}
              />
              <Inp
                label="Prénom"
                value={en.prenom || ''}
                onChange={(e) => {
                  const l = [...(f.enfantsList || [])];
                  l[ix] = { ...l[ix], prenom: e.target.value };
                  set('enfantsList', l);
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              <Inp
                label="Date de naissance"
                type="date"
                value={en.naissance || ''}
                onChange={(e) => {
                  const l = [...(f.enfantsList || [])];
                  l[ix] = { ...l[ix], naissance: e.target.value };
                  set('enfantsList', l);
                }}
              />
              <Inp
                label="Pays de résidence"
                value={en.pays || ''}
                onChange={(e) => {
                  const l = [...(f.enfantsList || [])];
                  l[ix] = { ...l[ix], pays: e.target.value };
                  set('enfantsList', l);
                }}
              />
            </div>
          </div>
        ))}
        <Btn
          label="＋ Ajouter un enfant"
          sm
          ghost
          color={C.green}
          onClick={() =>
            set('enfantsList', [
              ...(f.enfantsList || []),
              { nom: '', prenom: '', naissance: '', pays: 'Suisse' },
            ])
          }
        />
        <div style={{ fontSize: 8, color: C.muted, margin: '4px 0 2px', lineHeight: 1.5 }}>
          Pour les allocations familiales. Les <b>justificatifs</b> à joindre nécessiteront la mise
          en ligne.
        </div>

        {codeOuvrier && (
          <>
            {Sec(magasin ? '🔑 Accès à son espace employé' : '🔑 Accès sur chantier')}
            <Inp
              label="Code à 4 chiffres"
              inputMode="numeric"
              value={f.codePin ?? ''}
              onChange={(e) => set('codePin', e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder={f.codePinDefini ? 'Code enregistré — saisir pour le remplacer' : '••••'}
            />
            <div style={{ fontSize: 8, color: C.grey, lineHeight: 1.5, marginBottom: 2 }}>
              {magasin
                ? 'Ce code ouvre son espace employé : pointages et bons d’achat. Il n’est jamais réaffiché — le laisser vide conserve celui qui est enregistré.'
                : "Ce code ouvre l'espace de chantier : pointages, rapports, feuilles de régie. Il n'est jamais réaffiché — le laisser vide conserve celui qui est enregistré."}
              {f.codePinDefini && (
                <>
                  {' '}
                  <span
                    onClick={() => set('codePin', '')}
                    style={{ color: C.red, fontWeight: 800, cursor: 'pointer' }}
                  >
                    Retirer l'accès
                  </span>
                </>
              )}
            </div>
          </>
        )}

        {Sec('🏦 Coordonnées bancaires')}
        <Inp
          label="Titulaire du compte"
          value={f.titulaire || ''}
          onChange={(e) => set('titulaire', e.target.value)}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="IBAN"
            value={f.iban || ''}
            onChange={(e) => set('iban', e.target.value)}
            placeholder="CH.. .... .... .... .... ."
          />
          <Inp
            label="Banque"
            value={f.banque || ''}
            onChange={(e) => set('banque', e.target.value)}
          />
        </div>

        {Sec('📇 CCT / Qualification')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="CCT applicable"
            value={f.cct || ''}
            onChange={(e) => set('cct', e.target.value)}
            placeholder="CN construction…"
          />
          <Inp
            label="Classe / catégorie"
            value={f.classe || ''}
            onChange={(e) => set('classe', e.target.value)}
            placeholder="CE, A, B…"
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp
            label="CFC / diplôme"
            value={f.cfc || ''}
            onChange={(e) => set('cfc', e.target.value)}
          />
          <Inp
            label="Années d'expérience"
            type="number"
            value={f.exp || 0}
            onChange={(e) => set('exp', +e.target.value)}
          />
        </div>

        {Sec('🪪 Permis de séjour')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp label="Type de permis">
            {Sel('permis', ['Suisse', 'B', 'C', 'L', 'G', 'Ci', 'F'], 'Suisse')}
          </Inp>
          <Inp
            label="N° de permis"
            value={f.permisNum || ''}
            onChange={(e) => set('permisNum', e.target.value)}
          />
        </div>
        <Inp
          label="Date d'expiration du permis"
          type="date"
          value={f.permisExp || ''}
          onChange={(e) => set('permisExp', e.target.value)}
        />
        {f.permisExp &&
          (() => {
            try {
              const d = new Date(f.permisExp);
              const j = Math.round((d - new Date()) / 86400000);
              if (j <= 30 && j >= 0)
                return (
                  <div style={{ fontSize: 8.5, color: C.amber, fontWeight: 700, marginTop: -4 }}>
                    ⚠️ Le permis expire dans {j} jour(s).
                  </div>
                );
              if (j < 0)
                return (
                  <div style={{ fontSize: 8.5, color: C.red, fontWeight: 700, marginTop: -4 }}>
                    ⚠️ Permis expiré.
                  </div>
                );
            } catch (e) {}
            return null;
          })()}
        {Sec('📁 Dossier documents')}
        {(f.documents || []).map((doc, ix) => {
          const exp = doc.exp ? Math.round((new Date(doc.exp) - new Date()) / 86400000) : null;
          return (
            <div
              key={ix}
              style={{
                border: '1px solid ' + C.border,
                borderRadius: 9,
                padding: '9px 10px',
                marginBottom: 7,
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 7,
                  right: 9,
                  fontSize: 9,
                  color: C.red,
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
                onClick={() =>
                  set(
                    'documents',
                    (f.documents || []).filter((_, j) => j !== ix),
                  )
                }
              >
                ✕ retirer
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                <Inp label="Type">
                  <select
                    value={doc.type || ''}
                    onChange={(e) => {
                      const l = [...(f.documents || [])];
                      l[ix] = { ...l[ix], type: e.target.value };
                      set('documents', l);
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      border: '1px solid ' + C.border,
                      borderRadius: 9,
                      padding: '10px',
                      fontSize: 11,
                      background: '#fff',
                    }}
                  >
                    {[
                      "Pièce d'identité",
                      'Permis de séjour',
                      'Contrat signé',
                      'Avenant',
                      'CFC / Diplôme',
                      'Certificat',
                      'Assurance',
                      'Allocations familiales',
                      'Autre',
                    ].map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </Inp>
                <Inp
                  label="Nom / référence"
                  value={doc.nom || ''}
                  onChange={(e) => {
                    const l = [...(f.documents || [])];
                    l[ix] = { ...l[ix], nom: e.target.value };
                    set('documents', l);
                  }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                <Inp
                  label="Expiration (si applicable)"
                  type="date"
                  value={doc.exp || ''}
                  onChange={(e) => {
                    const l = [...(f.documents || [])];
                    l[ix] = { ...l[ix], exp: e.target.value };
                    set('documents', l);
                  }}
                />
                <Inp label="Statut">
                  <select
                    value={doc.statut || 'reçu'}
                    onChange={(e) => {
                      const l = [...(f.documents || [])];
                      l[ix] = { ...l[ix], statut: e.target.value };
                      set('documents', l);
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      border: '1px solid ' + C.border,
                      borderRadius: 9,
                      padding: '10px',
                      fontSize: 11,
                      background: '#fff',
                    }}
                  >
                    <option value="reçu">✅ Reçu</option>
                    <option value="manquant">⏳ Manquant</option>
                  </select>
                </Inp>
              </div>
              {exp !== null && exp <= 30 && exp >= 0 && (
                <div style={{ fontSize: 8.5, color: C.amber, fontWeight: 700, marginTop: -2 }}>
                  ⚠️ Expire dans {exp} jour(s).
                </div>
              )}
              {exp !== null && exp < 0 && (
                <div style={{ fontSize: 8.5, color: C.red, fontWeight: 700, marginTop: -2 }}>
                  ⚠️ Document expiré.
                </div>
              )}
              <FichierDossier
                employeId={f.id}
                piece={doc}
                onChange={(p) => {
                  const l = [...(f.documents || [])];
                  l[ix] = p;
                  set('documents', l);
                }}
              />
            </div>
          );
        })}
        <Btn
          label="＋ Ajouter un document"
          sm
          ghost
          color={C.green}
          onClick={() =>
            set('documents', [
              ...(f.documents || []),
              { type: "Pièce d'identité", nom: '', exp: '', statut: 'reçu' },
            ])
          }
        />
        <div style={{ fontSize: 8, color: C.muted, margin: '4px 0 2px', lineHeight: 1.5 }}>
          Suivi du dossier : pièce d'identité, permis, contrat signé, diplômes, certificats,
          assurances, allocations… Les fichiers joints sont conservés sur le serveur et ne se
          consultent que par la direction.
        </div>

        {Sec('🚪 Fin de contrat / Départ')}
        <Inp label="Statut de l'employé">
          <select
            value={f.parti ? 'Parti' : 'Actif'}
            onChange={(e) => set('parti', e.target.value === 'Parti')}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid ' + C.border,
              borderRadius: 9,
              padding: '10px',
              fontSize: 11,
              background: '#fff',
            }}
          >
            <option value="Actif">Actif</option>
            <option value="Parti">Parti (contrat terminé)</option>
          </select>
        </Inp>
        {f.parti && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
            <Inp
              label="Date de départ"
              type="date"
              value={f.dateDepart || ''}
              onChange={(e) => set('dateDepart', e.target.value)}
            />
            <Inp
              label="Motif du départ"
              value={f.motifDepart || ''}
              onChange={(e) => set('motifDepart', e.target.value)}
              placeholder="Démission, fin CDD…"
            />
          </div>
        )}
        {f.parti && (
          <div style={{ fontSize: 8, color: C.muted, margin: '2px 0 6px', lineHeight: 1.5 }}>
            À la clôture : vacances/heures restantes, <b>certificat de travail</b> et{' '}
            <b>certificat de salaire</b> à générer. Le dossier reste archivé (15 ans).
          </div>
        )}
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: C.muted,
            marginBottom: 5,
            textTransform: 'uppercase',
          }}
        >
          Couleur
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
          {COLS.map((c) => (
            <div
              key={c}
              onClick={() => set('col', c)}
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                background: c,
                cursor: 'pointer',
                border: f.col === c ? '3px solid ' + C.ink : '2px solid #fff',
                boxShadow: '0 0 0 1px ' + C.border,
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 7 }}>
          <div style={{ flex: 1 }}>
            <Btn
              label={f.id ? '✓ Enregistrer' : "✓ Créer l'employé"}
              color={C.green}
              full
              disabled={!f.nom?.trim()}
              onClick={onSave}
            />
          </div>
          {f.id && onDelete && (
            <Btn label="📦 Retirer de l’effectif" ghost danger color={C.red} onClick={onDelete} />
          )}
          <Btn label="Annuler" ghost color={C.grey} onClick={() => setEmpForm(null)} />
        </div>
      </div>
    </Card>
  );
}

/** Vrai pour une raison individuelle : le patron y est indépendant, pas salarié. */
export function estIndependant(forme) {
  const v = String(forme || '').trim();
  return v === '' || v === 'Indépendant';
}

/**
 * Le statut du patron, sur sa propre fiche.
 *
 * <p>En raison individuelle, le patron est indépendant : il n'est pas assuré
 * contre le chômage et le 2e pilier ne lui est pas obligatoire. Il paie son
 * AVS d'indépendant directement à sa caisse de compensation, sur son bénéfice.
 * Patron d'une Sàrl ou d'une SA, il en est salarié : les assurances
 * s'appliquent comme à un employé.
 */
function StatutDuPatron({ forme, soumisAC, soumisLPP, onIndependant }) {
  const independant = estIndependant(forme);
  return (
    <div
      data-testid="statut-patron"
      style={{
        background: '#FFF7E6',
        border: '1px solid #F5D08A',
        borderRadius: 9,
        padding: '10px 11px',
        margin: '2px 0 8px',
        fontSize: 9.5,
        lineHeight: 1.5,
        color: C.ink,
      }}
    >
      <div style={{ fontWeight: 800, color: C.navy, marginBottom: 4 }}>
        👤 Votre fiche de patron
      </div>
      {independant ? (
        <>
          <div>
            Indépendant (raison individuelle) : vous n’êtes pas soumis à l’assurance chômage, et le
            2e pilier n’est pas obligatoire pour vous. Votre AVS d’indépendant se paie directement à
            votre caisse de compensation, sur votre bénéfice.
          </div>
          {soumisAC || soumisLPP ? (
            <button
              type="button"
              onClick={onIndependant}
              style={{
                marginTop: 7,
                border: 'none',
                borderRadius: 8,
                padding: '8px 10px',
                fontSize: 10,
                fontWeight: 800,
                cursor: 'pointer',
                background: C.navy,
                color: '#fff',
              }}
            >
              Retirer le chômage et le 2e pilier
            </button>
          ) : (
            <div style={{ marginTop: 5, fontWeight: 700, color: C.green }}>
              ✓ Chômage et 2e pilier retirés. Affilié volontairement à une caisse de pension ?
              Réactivez le 2e pilier ci-dessous.
            </div>
          )}
        </>
      ) : (
        <div>
          {trf(
            'Patron salarié de votre {0} : l’AVS, le chômage et le 2e pilier s’appliquent comme pour un employé. La forme juridique se règle dans Réglages → Société.',
            forme,
          )}
        </div>
      )}
    </div>
  );
}
